import React, { useState } from "react";
import toast from "react-hot-toast";
import { cancelOrderItem } from "../services/orderService";
import { showErrorToast } from "../utils/errorHandler";

const CancelItemSidebar = ({ isOpen, onClose, item, orderId, onSuccess }) => {
  const [cancellationReason, setCancellationReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [closing, setClosing] = useState(false);

  // Prevent body scroll when sidebar is open
  React.useEffect(() => {
    if (isOpen) {
      document.documentElement.style.setProperty('overflow', 'hidden', 'important');
      document.body.style.setProperty('overflow', 'hidden', 'important');
    } else {
      document.documentElement.style.removeProperty('overflow');
      document.body.style.removeProperty('overflow');
    }

    return () => {
      document.documentElement.style.removeProperty('overflow');
      document.body.style.removeProperty('overflow');
    };
  }, [isOpen]);

  React.useEffect(() => {
    if (isOpen && item) {
      setMounted(true);
      setClosing(false);
    } else if (mounted) {
      setClosing(true);
      setTimeout(() => {
        setMounted(false);
        setClosing(false);
      }, 300);
    }
  }, [isOpen, item]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      setMounted(false);
      setClosing(false);
      setCancellationReason("");
      onClose();
    }, 300);
  };

  const handleCancel = async () => {
    if (!cancellationReason) {
      toast.error("Please select a reason for cancellation");
      return;
    }
    setLoading(true);
    try {
      await cancelOrderItem(orderId, item._id, cancellationReason, "");
      toast.success("Item cancelled successfully");
      if (onSuccess) onSuccess();
      handleClose();
    } catch (err) {
      showErrorToast(err, "Failed to cancel item");
    } finally {
      setLoading(false);
    }
  };

  if (!mounted) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/40 z-[10000]"
        onClick={handleClose}
      />

      {/* Sidebar */}
      <div
        className={`fixed top-0 right-0 bottom-0 w-full sm:max-w-md bg-white z-[10001] shadow-2xl flex flex-col drawer-panel ${
          closing ? "drawer-slide-out" : "drawer-slide-in"
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-2">
            <h3 className="text-base text-black font-bdogrotesk">Cancel Item</h3>
            <button
              onClick={handleClose}
              className="text-black text-base font-bdogrotesk font-medium"
            >
              Close
            </button>
          </div>

          {/* Scrollable Body */}
          <div className="flex-1 overflow-y-auto no-scrollbar px-4 py-4 space-y-6">
            {/* Heading */}
            <h3 className="text-4xl font-bdogrotesk font-medium leading-tight text-black">
              Changed your mind?{" "}
              No worries! We're all for shopping mindfully.
            </h3>

            {/* Product Display */}
            <div className="flex gap-4">
              {/* Image */}
              <div className="w-40 h-52 bg-gray-50 shrink-0">
                {item.image && (
                  <img
                    src={item.image}
                    alt={item.productName}
                    className="w-full h-full object-cover"
                  />
                )}
              </div>

              {/* Details — flex-col with justify-between so note sits at image bottom */}
              <div className="flex-1 flex flex-col justify-between">
                <div className="space-y-1">
                  <p className="text-base font-medium text-black font-bdogrotesk">
                    {item.productName || "Product"} -  {item.size}
                  </p>
                  
                  <p className="text-sm text-black font-bdogrotesk">
                    Quantity: {item.quantity}
                  </p>
                  <p className="text-base font-medium text-black font-bdogrotesk">
                    ₹{item.price?.toLocaleString("en-IN")}
                  </p>
                </div>
                {/* Aligned to bottom of image */}
                <p className="text-sm text-gray-400 font-medium font-bdogrotesk leading-tight">
                  Cancellation window closes after 24 hours of placing your order.
                  After that we've started crafting the piece just for you!
                </p>
              </div>
            </div>
          </div>

          {/* Footer — reason + button pinned to bottom */}
          <div className="p-4  flex flex-col gap-3">
            {/* Reason dropdown */}
            <div>
              <select
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                className="w-full py-3 px-3 text-sm border border-gray-300 focus:outline-none focus:border-black bg-white font-bdogrotesk"
              >
                <option value="">Tell us what went wrong</option>
                <option value="Ordered by mistake">Ordered by mistake</option>
                <option value="Found better price elsewhere">Found better price elsewhere</option>
                <option value="Changed my mind">Changed my mind</option>
                <option value="Delivery time too long">Delivery time too long</option>
                <option value="Want to modify order">Want to modify order</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Cancel button — Add To Cart style */}
            <button
              type="button"
              onClick={handleCancel}
              disabled={loading || !cancellationReason}
              className="group w-full bg-black text-white px-3 py-3 flex items-center justify-between text-[15px] font-semibold tracking-wide font-bdogrotesk disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="relative h-5 overflow-hidden">
                <div className="relative flex flex-col transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
                  <span className="flex font-bdogrotesk h-5 items-center">
                    {loading ? "Processing..." : "Cancel This Item"}
                  </span>
                  <span className="flex h-5 items-center">
                    {loading ? "Processing..." : "Cancel This Item"}
                  </span>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CancelItemSidebar;
